import { LEAD_STATUS_LABELS, LEAD_STATUS_SEVERITY } from '@sitewise/shared';

import type { StatPillTone } from './stat-pill-tone.js';
import type { LeadStatus, LeadStatusSeverity } from '@sitewise/shared';


/** UI shim for the `lead_status` enum. Labels and the canonical
 *  severity assignment come from `@sitewise/shared`; this module is
 *  only responsible for projecting that severity onto the
 *  `StatPill`'s tone palette (which uses semantic names rather than
 *  the severity vocabulary). */
export { LEAD_STATUS_LABELS };

/** Severity → StatPill tone projection. The severity helper in
 *  `packages/ui` is the source of truth for actual pixels — this
 *  table just chooses which named `StatPill` tone (success/warning/
 *  danger/info/neutral) carries the same meaning. `success` and
 *  `low`/`info` map to `success`/`info`/`neutral` respectively; the
 *  three "needs attention" severities map to danger/warning. */
const SEVERITY_TO_TONE: Record<LeadStatusSeverity, StatPillTone> = {
  critical: 'danger',
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
  info: 'info',
  success: 'success',
};

/** Map enum value → StatPill tone via the canonical severity. The
 *  per-status entries below are derived (not hand-curated) so the
 *  whole project stays in lock-step with the design system. */
export const LEAD_STATUS_TONE: Record<LeadStatus, StatPillTone> = Object.fromEntries(
  (Object.keys(LEAD_STATUS_SEVERITY) as LeadStatus[]).map((k) => [
    k,
    SEVERITY_TO_TONE[LEAD_STATUS_SEVERITY[k]],
  ]),
) as Record<LeadStatus, StatPillTone>;

const ALL_LEAD_STATUSES = Object.keys(LEAD_STATUS_LABELS) as LeadStatus[];

export function isLeadStatus(value: unknown): value is LeadStatus {
  return typeof value === 'string' && (ALL_LEAD_STATUSES as string[]).includes(value);
}
